import {
  applyTranslationDialog,
  type DuplicateSyncKey,
  findDuplicateSyncKeys,
  normalizeSyncKey,
  resolveFieldLabel,
} from "@repo/shared";
import {
  type FinancialFieldDefinition,
  type FsClientType,
  fieldAppliesTo,
} from "@repo/shared/types";
import { Building2, GripVertical, Info, Trash2, User, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Breadcrumb } from "@/components/breadcrumb";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TranslationButton } from "@/components/ui/translation-button";
import { ApiError } from "@/lib/api/_client";
import {
  useFinancialSituationModel,
  useUpdateFinancialSituationModel,
} from "@/lib/hooks/use-financial-situation-models";
import { useUserLanguage } from "@/lib/i18n";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { DerivedStatusesEditor } from "./derived-statuses-editor";
import { T } from "./model-editor.i18n";
import { ModelEditorLoader } from "./model-editor-loader";
import { ModelSettingsEditor } from "./model-settings-editor";

// QP-176/QL-140 — the editor presents Persons, Companies and Groups as
// separate views; a field belongs to whichever type `fieldAppliesTo` resolves
// (absent clientTypes = Person-only, the pre-QP-176 default). New fields are
// tagged for the view they were created in — the storage stays one list per
// category so the widget/engine filtering is unchanged.

type ModelEditorProps = {
  id: string;
};

export function ModelEditor({ id }: ModelEditorProps) {
  const { userLanguage, tenantSupportedLanguages } = useUserLanguage();
  const t = useT(T);
  const { model, isLoading } = useFinancialSituationModel(id);
  const updateModel = useUpdateFinancialSituationModel();
  const isPending = updateModel.isPending;

  // Field state
  const [incomeFields, setIncomeFields] = useState<FinancialFieldDefinition[]>([]);
  const [assetFields, setAssetFields] = useState<FinancialFieldDefinition[]>([]);
  const [liabilityFields, setLiabilityFields] = useState<FinancialFieldDefinition[]>([]);
  const [commitmentFields, setCommitmentFields] = useState<FinancialFieldDefinition[]>([]);

  // Drag state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragCategory, setDragCategory] = useState<
    "income" | "assets" | "liabilities" | "commitments" | null
  >(null);

  // Active field category tab
  const [activeFieldTab, setActiveFieldTab] = useState<
    "income" | "assets" | "liabilities" | "commitments"
  >("income");

  // QP-176/QL-140 — Persons / Companies / Groups are edited as separate views.
  const [activeClientType, setActiveClientType] = useState<FsClientType>("Person");

  // Sync state when model loads
  useEffect(() => {
    if (model) {
      setIncomeFields(model.incomeFields ?? []);
      setAssetFields(model.assetFields ?? []);
      setLiabilityFields(model.liabilityFields ?? []);
      setCommitmentFields(model.commitmentFields ?? []);
    }
  }, [model]);

  // QL-143 — on open the four lists are copies of the saved model, so Save had
  // nothing to send; it now waits for the editor to actually diverge. Field
  // objects are plain JSON built by spreading the stored ones, so key order is
  // preserved and a serialised compare is a faithful equality test.
  // Declared above the early returns below so the hook order never changes.
  const isDirty = useMemo(() => {
    if (!model) return false;
    const same = (
      a: FinancialFieldDefinition[],
      b: FinancialFieldDefinition[] | null | undefined,
    ) => JSON.stringify(a) === JSON.stringify(b ?? []);
    return !(
      same(incomeFields, model.incomeFields) &&
      same(assetFields, model.assetFields) &&
      same(liabilityFields, model.liabilityFields) &&
      same(commitmentFields, model.commitmentFields)
    );
  }, [model, incomeFields, assetFields, liabilityFields, commitmentFields]);

  // QL-330 — the same uniqueness rule the server enforces on save: a sync key
  // may not repeat among the fields of one client type. Computed over all
  // four lists so a collision across categories is caught too; Save is held
  // while any exists.
  const duplicateSyncKeys = useMemo<DuplicateSyncKey[]>(
    () => findDuplicateSyncKeys({ incomeFields, assetFields, liabilityFields, commitmentFields }),
    [incomeFields, assetFields, liabilityFields, commitmentFields],
  );
  const isSyncKeyDuplicate = (field: FinancialFieldDefinition) => {
    const key = normalizeSyncKey(field.syncKey);
    if (!key) return false;
    return duplicateSyncKeys.some((d) => d.syncKey === key && fieldAppliesTo(field, d.clientType));
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Breadcrumb
          items={[
            { label: t.settings, href: "/settings" },
            { label: t.financialSituationModels, href: "/settings/financial-situation-models" },
            { label: t.loading },
          ]}
        />
        <ModelEditorLoader />
      </div>
    );
  }

  if (!model) {
    return <div className="py-12 text-center text-muted-foreground">{t.notFound}</div>;
  }

  const handleSave = async () => {
    // QL-330 — store sync keys trimmed and never as an empty string; the
    // server sanitizes too, this just keeps the editor's dirty check honest.
    const clean = (fields: FinancialFieldDefinition[]) =>
      fields.map((f) => {
        const { syncKey, ...rest } = f;
        const key = normalizeSyncKey(syncKey);
        return key ? { ...rest, syncKey: key } : rest;
      });
    try {
      await updateModel.mutateAsync({
        id,
        data: {
          incomeFields: clean(incomeFields),
          assetFields: clean(assetFields),
          liabilityFields: clean(liabilityFields),
          commitmentFields: clean(commitmentFields),
        },
      });
      toast.success(t.changesSaved);
    } catch (err) {
      toast.error(t.error, {
        description: err instanceof ApiError ? err.message : t.failedToSave,
      });
    }
  };

  const addField = (category: "income" | "assets" | "liabilities" | "commitments") => {
    // QL-39 — a field is a label plus flags: no taxonomy type, no help text.
    const newField: FinancialFieldDefinition = {
      id: `${category}_${Date.now()}`,
      label: t.newField,
      labelI18n: { [userLanguage]: t.newField },
    };

    // QP-176/QL-140 — tag the field for the view it was created in. Person
    // fields keep the absent-clientTypes default (= Person-only per
    // fieldAppliesTo). The id needs no type prefix — the tag is what decides
    // applicability, and a `group_` prefix would collide with the clones the
    // QL-140 data migration creates.
    if (activeClientType !== "Person") {
      newField.clientTypes = [activeClientType];
    }

    if (category === "liabilities") {
      newField.hasMonthlyPayment = true;
    }

    switch (category) {
      case "income":
        setIncomeFields([...incomeFields, newField]);
        break;
      case "assets":
        setAssetFields([...assetFields, newField]);
        break;
      case "liabilities":
        setLiabilityFields([...liabilityFields, newField]);
        break;
      case "commitments":
        setCommitmentFields([...commitmentFields, newField]);
        break;
    }
  };

  const updateField = (
    category: "income" | "assets" | "liabilities" | "commitments",
    index: number,
    updates: Partial<FinancialFieldDefinition>,
  ) => {
    const updateList = (fields: FinancialFieldDefinition[]) => {
      const newFields = [...fields];
      newFields[index] = { ...newFields[index], ...updates };
      return newFields;
    };

    switch (category) {
      case "income":
        setIncomeFields(updateList(incomeFields));
        break;
      case "assets":
        setAssetFields(updateList(assetFields));
        break;
      case "liabilities":
        setLiabilityFields(updateList(liabilityFields));
        break;
      case "commitments":
        setCommitmentFields(updateList(commitmentFields));
        break;
    }
  };

  const removeField = (
    category: "income" | "assets" | "liabilities" | "commitments",
    index: number,
  ) => {
    switch (category) {
      case "income":
        setIncomeFields(incomeFields.filter((_, i) => i !== index));
        break;
      case "assets":
        setAssetFields(assetFields.filter((_, i) => i !== index));
        break;
      case "liabilities":
        setLiabilityFields(liabilityFields.filter((_, i) => i !== index));
        break;
      case "commitments":
        setCommitmentFields(commitmentFields.filter((_, i) => i !== index));
        break;
    }
  };

  const handleDragStart = (
    category: "income" | "assets" | "liabilities" | "commitments",
    index: number,
  ) => {
    setDraggedIndex(index);
    setDragCategory(category);
  };

  const handleDragOver = (
    e: React.DragEvent,
    category: "income" | "assets" | "liabilities" | "commitments",
    index: number,
  ) => {
    e.preventDefault();
    if (draggedIndex === null || dragCategory !== category || draggedIndex === index) return;

    const getFields = () => {
      switch (category) {
        case "income":
          return incomeFields;
        case "assets":
          return assetFields;
        case "liabilities":
          return liabilityFields;
        case "commitments":
          return commitmentFields;
      }
    };

    const setFields = (newFields: FinancialFieldDefinition[]) => {
      switch (category) {
        case "income":
          setIncomeFields(newFields);
          break;
        case "assets":
          setAssetFields(newFields);
          break;
        case "liabilities":
          setLiabilityFields(newFields);
          break;
        case "commitments":
          setCommitmentFields(newFields);
          break;
      }
    };

    const fields = getFields();
    const newFields = [...fields];
    const draggedField = newFields[draggedIndex];
    newFields.splice(draggedIndex, 1);
    newFields.splice(index, 0, draggedField);

    setFields(newFields);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragCategory(null);
  };

  const renderFieldEditor = (
    category: "income" | "assets" | "liabilities" | "commitments",
    fields: FinancialFieldDefinition[],
  ) => (
    <div className="space-y-2">
      <ul className="space-y-2 list-none p-0 m-0">
        {/* QP-176 — render only the active client type's fields, but keep the
            REAL index into the stored list so update/remove/drag stay correct. */}
        {fields
          .map((field, index) => ({ field, index }))
          .filter(({ field }) => fieldAppliesTo(field, activeClientType))
          .map(({ field, index }) => {
            const duplicate = isSyncKeyDuplicate(field);
            return (
              <li
                key={field.id}
                draggable
                onDragStart={() => handleDragStart(category, index)}
                onDragOver={(e) => handleDragOver(e, category, index)}
                onDragEnd={handleDragEnd}
                className={`flex items-start gap-3 p-3 border rounded-lg bg-muted/30 cursor-move transition-opacity ${
                  draggedIndex === index && dragCategory === category ? "opacity-50" : ""
                }`}
              >
                <div className="text-muted-foreground mt-2">
                  <GripVertical className="h-4 w-4" />
                </div>

                {/* QL-39/QL-330 — Label | Sync key | flags. The former type
                    select and help-text input are gone. */}
                <div className="flex-1 grid grid-cols-[minmax(0,3fr)_minmax(0,2fr)_auto] gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs">{t.label}</Label>
                    <div className="flex items-center gap-1">
                      <Input
                        // Intended: `label` is the last text typed, in the designer's language.
                        value={resolveFieldLabel(field, userLanguage)}
                        onChange={(e) =>
                          updateField(category, index, {
                            label: e.target.value,
                            labelI18n: { ...field.labelI18n, [userLanguage]: e.target.value },
                          })
                        }
                        placeholder={t.fieldLabelPlaceholder}
                        onMouseDown={(e) => e.stopPropagation()}
                      />
                      <TranslationButton
                        value={{
                          ...field.labelI18n,
                          [userLanguage]: resolveFieldLabel(field, userLanguage),
                        }}
                        onSave={(labelI18n) =>
                          updateField(
                            category,
                            index,
                            applyTranslationDialog(
                              field,
                              labelI18n as Record<string, string>,
                              userLanguage,
                            ),
                          )
                        }
                        supportedLanguages={tenantSupportedLanguages}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs flex items-center gap-1">
                      {t.syncKey}
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="h-3 w-3 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent className="max-w-xs">{t.syncKeyHelp}</TooltipContent>
                      </Tooltip>
                    </Label>
                    <Input
                      value={field.syncKey ?? ""}
                      onChange={(e) => updateField(category, index, { syncKey: e.target.value })}
                      placeholder={t.syncKeyPlaceholder}
                      onMouseDown={(e) => e.stopPropagation()}
                      aria-invalid={duplicate || undefined}
                      className={cn("font-mono", duplicate && "border-destructive")}
                    />
                    {duplicate && <p className="text-xs text-destructive">{t.syncKeyDuplicate}</p>}
                  </div>

                  <div className="flex flex-col gap-3 pt-6">
                    <div className="flex items-center gap-2">
                      <Switch
                        aria-label={t.required}
                        checked={field.required === true}
                        onCheckedChange={(checked) =>
                          updateField(category, index, { required: checked || undefined })
                        }
                      />
                      <Label className="text-xs flex items-center gap-1">
                        {t.required}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3 w-3 text-muted-foreground" />
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">{t.requiredHelp}</TooltipContent>
                        </Tooltip>
                      </Label>
                    </div>
                    {category === "liabilities" && (
                      <div className="flex items-center gap-2">
                        <Switch
                          aria-label={t.monthly}
                          checked={field.hasMonthlyPayment}
                          onCheckedChange={(checked) =>
                            updateField(category, index, { hasMonthlyPayment: checked })
                          }
                        />
                        <Label className="text-xs">{t.monthly}</Label>
                      </div>
                    )}
                  </div>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  tooltip={t.removeField}
                  className="mt-5"
                  onClick={() => removeField(category, index)}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </li>
            );
          })}
      </ul>

      {activeClientType !== "Person" &&
        !fields.some((f) => fieldAppliesTo(f, activeClientType)) && (
          <p className="text-sm text-muted-foreground text-center py-2">
            {activeClientType === "Company" ? t.noCompanyFields : t.noGroupFields}
          </p>
        )}

      <Button variant="outline" onClick={() => addField(category)} className="w-full">
        {t.addField}
      </Button>
    </div>
  );

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={[
          { label: t.settings, href: "/settings" },
          { label: t.financialSituationModels, href: "/settings/financial-situation-models" },
          { label: model.name },
        ]}
        title={model.name}
        description={t.configureFieldDefinitions}
      />

      {/* QP-176/QL-140 — page-level Persons/Companies/Groups view switch:
          governs the field lists AND the capacity settings below. */}
      <div className="flex justify-end">
        <div className="flex items-center gap-1 rounded-lg border p-1">
          <Button
            type="button"
            variant={activeClientType === "Person" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setActiveClientType("Person")}
            className="h-8 px-3 gap-2"
          >
            <User className="h-4 w-4" />
            {t.persons}
          </Button>
          <Button
            type="button"
            variant={activeClientType === "Company" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setActiveClientType("Company")}
            className="h-8 px-3 gap-2"
          >
            <Building2 className="h-4 w-4" />
            {t.companies}
          </Button>
          <Button
            type="button"
            variant={activeClientType === "Group" ? "secondary" : "ghost"}
            size="sm"
            onClick={() => setActiveClientType("Group")}
            className="h-8 px-3 gap-2"
          >
            <Users className="h-4 w-4" />
            {t.groups}
          </Button>
        </div>
      </div>

      {/* Field Definitions — QP-176/QL-140: Persons, Companies and Groups are
          edited as separate views over the same per-category lists (fields are
          tagged with the view they were created in). */}
      <Card>
        <CardHeader>
          <CardTitle>{t.fieldDefinitions}</CardTitle>
          <CardDescription>{t.fieldDefinitionsDescription}</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Simple underline navigation for field categories */}
          <nav className="flex gap-6 border-b mb-4">
            {(
              [
                { key: "income", label: t.income, fields: incomeFields },
                { key: "assets", label: t.assets, fields: assetFields },
                { key: "liabilities", label: t.liabilities, fields: liabilityFields },
                { key: "commitments", label: t.commitments, fields: commitmentFields },
              ] as const
            )
              .map((tab) => ({
                ...tab,
                count: tab.fields.filter((f) => fieldAppliesTo(f, activeClientType)).length,
              }))
              .map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveFieldTab(tab.key)}
                  className={cn(
                    "pb-2 text-sm font-medium transition-colors relative",
                    activeFieldTab === tab.key
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {tab.label} ({tab.count})
                  {activeFieldTab === tab.key && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                  )}
                </button>
              ))}
          </nav>

          {/* Field content — QP-176/QL-140: one view per client type over the
              same per-category lists. */}
          {activeFieldTab === "income" && renderFieldEditor("income", incomeFields)}
          {activeFieldTab === "assets" && renderFieldEditor("assets", assetFields)}
          {activeFieldTab === "liabilities" && renderFieldEditor("liabilities", liabilityFields)}
          {activeFieldTab === "commitments" && renderFieldEditor("commitments", commitmentFields)}

          <div className="flex justify-end pt-6 mt-6 border-t">
            <Button
              onClick={handleSave}
              loading={isPending}
              disabled={!isDirty || duplicateSyncKeys.length > 0}
            >
              {t.save}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* QP-195/QL-140 — derived statuses (model-level: person + company +
          group source fields) */}
      <DerivedStatusesEditor
        modelId={id}
        statuses={model.statuses ?? []}
        fieldGroups={{
          income: incomeFields,
          assets: assetFields,
          liabilities: liabilityFields,
          commitments: commitmentFields,
        }}
      />

      {/* QP-176/QP-183 — business-rule settings (one-of-group, expecting-changes,
          investment capacity + deposit gate). Asset-field options come from the
          SAVED model state — save new fields first before selecting them. The
          capacity section follows the page-level client-type switch. */}
      <ModelSettingsEditor
        modelId={id}
        settings={model.settings ?? {}}
        assetFields={model.assetFields ?? []}
        activeClientType={activeClientType}
      />
    </div>
  );
}
